import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { IonAccordionGroup } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { ColumnMode, DatatableComponent } from "@swimlane/ngx-datatable";
import { Subscription } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";
import { Gateway } from "src/components/shared-components/class/general-classes/gateway";
import {
  Column,
  GatewayNameForTable,
  ItemHistory,
  TableInfo,
} from "src/components/shared-components/class/general-classes/item-history";
import { NgxDatatableMessages } from "src/model/class/general classes/ngx-datatable-messages";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ToastType } from "src/components/shared-components/enum/enumMisc";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import {
  ColumnTableType,
  HideShowColumn,
} from "src/components/shared-components/enum/enumTableHistory";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { DateFormatterFilterService } from "src/service/date-formatter-filter.service";
import { LoaderService } from "src/service/loader.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { ToastService } from "src/service/toast.service";
import { TranslationService } from "src/service/translation.service";
import { listGateway } from "src/state/selectors/gateway.selectors";

@Component({
    selector: "app-generic-table",
    templateUrl: "./generic-table.component.html",
    styleUrls: ["./generic-table.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GenericTableComponent
  implements OnInit, OnChanges, OnDestroy, iTranslation {
  @Input() tableInfo: TableInfo = null;
  @Input() selected: any[] = [];
  @Input() isUserInPage: boolean = true;
  @Input() tableInfoSecond: TableInfo = null;
  @Input() selectedSecond: any[] = [];
  @Input() showAllCol: boolean = false;
  @Input() showAllColSecond: boolean = false;
  @Output() filtersEmitter = new EventEmitter<string>();
  @Output() selectionEmitter = new EventEmitter<any>();
  @Output() reloadEmitter = new EventEmitter<any>();
  @Output() selectionAppEmitter = new EventEmitter<ItemHistory>();
  @ViewChild("genericTable", { static: false })
  genericTable: DatatableComponent;
  @ViewChild("secondTable", { static: false }) secondTable: DatatableComponent;
  @ViewChild("accordionGroup", { static: false })
  accordionGroup: IonAccordionGroup;

  private _logContext: string = "generic-history.component";

  private _translateSubscription: Subscription = null;
  private _networkSubscription: Subscription = null;

  private _columsCopy: Column[] = [];
  private _columnsSecondCopy: Column[] = [];
  private _lblErrorDate: string = "";
  private _fromDay: string = "fromDay";
  private _toDay: string = "toDay";
  private _fromDaySolution: string = "fromDaySolution";
  private _toDaySolution: string = "toDaySolution";
  private _fromTime: string = "fromTime";
  private _toTime: string = "toTime";
  private _idAll: number = -1;
  private _intervalResizeCol: number = 500;
  private _middleWidth: number = 1500;
  private _smallWidth: number = 1000;

  private _gatewaysNames: Array<GatewayNameForTable> =
    new Array<GatewayNameForTable>();

  public columnMode = ColumnMode;
  public columnType = ColumnTableType;
  /* public comparator = Comparator; */

  public customComparator = (valueA, valueB, rowA, rowB, sortDirection?) =>
    this.onSort(valueA, valueB, rowA, rowB, sortDirection);

  public ngxDatatableMessages: NgxDatatableMessages =
    new NgxDatatableMessages();
  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  public mouseOut: boolean = true;

  constructor(
    private _loaderService: LoaderService,
    private _toastService: ToastService,
    private _translateService: TranslateService,
    private _networkService: NetworkService,
    private _store: Store,
    private _loggerService: LoggerService,
    private _translationService: TranslationService,
    private _dateFormatterService: DateFormatterFilterService
  ) { }

  public ngOnInit(): void {
    this._columsCopy = this.tableInfo.Columns;
    this._columnsSecondCopy = this.tableInfoSecond?.Columns;
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (this.selected.length > 0) {
      let selElems = [];
      for (let idx = 0; idx < this.selected.length; idx++) {
        let selElem = this.tableInfo.List.find(
          (cg) => cg.id === this.selected[idx].id
        );
        if (selElem) {
          selElems.push(selElem);
        }
      }

      this.selected = selElems;
    } else if (this.selectedSecond.length > 0) {
      let fatherElement = null;
      for (let i = 0; i < this.tableInfo.List.length; i++) {
        for (let j = 0; j < this.tableInfo.List[i].details.length; j++) {
          if (
            this.selectedSecond[0].id === this.tableInfo.List[i].details[j].id
          ) {
            this.selectedSecond = [];
            this.selected = [];
            this.selectedSecond.push(this.tableInfo.List[i].details[j]);
            fatherElement = this.tableInfo.List[i];
            break;
          }
        }
      }
      this.changeExpand(null, fatherElement, false);
    }
    this.recalcTable();
    await this.loadGateways();
  }

  public ngOnDestroy(): void {
    this._translateSubscription?.unsubscribe();
    this._networkSubscription?.unsubscribe();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.startDateBiggerEndDate", "dictionary.entriesAmount"])
      .subscribe((translation) => {
        this._lblErrorDate = translation["dictionary.startDateBiggerEndDate"];
        this.ngxDatatableMessages.totalMessage =
          translation["dictionary.entriesAmount"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * With the values obtained from the filters,
   * it creates the query to use for searches.
   * @param filterData data from filters
   */
  public async onFilterRequest(filterData: Map<string, string>): Promise<void> {
    await this._loaderService.presentLoaderWaiting();
    let queryString = "";
    let filData = this._dateFormatterService.formatValuesFilter(
      filterData,
      this._lblErrorDate,
      this.tableInfo
    );
    if (filData) {
      filData.forEach((value, key) => {
        if (queryString.length > 0) queryString += "&";
        queryString += key + "=" + encodeURIComponent(value);
      });
      this.filtersEmitter.emit(queryString);
      this.recalcTable();
      if (this.tableInfo.IsApp) {
        this.closeAccordion();
      }
    }
  }

  /**
   * Outputs the object corresponding to the selected
   * row in the table (only if the table allows selection).
   */
  public selectionChanged(event: any, value: number): void {
    if (this.tableInfo.SelectionType) {
      this.selected = [];
      this.selectedSecond = [];
      if (value === 1) {
        this.selected.push(event.selected);
      } else {
        this.selectedSecond.push(event.selected);
      }

      this.selectionEmitter.emit(event.selected);
      this.recalcTable();
    }
  }

  /**
   * When the button is pressed to open the internal table,
   * it prevents the row in which the button is located from being selected.
   */
  public onActivate(event: any): void {
    if (event.type === "mouseenter") {
      this.fireEvent(event.event);
    }
  }

  public onSelectFromApp(index: number) {
    this.selectionAppEmitter.emit(this.tableInfo.List[index]);
  }

  /**
   * Returns the data, belonging to the row, necessary for the internal table.
   */
  public getDetailRows(row: any): any {
    return row.details;
  }

  /**
   * Stop the propagation of the event.
   */
  public fireEvent(event: any): void {
    if (event != null) {
      event.preventDefault();
      event.stopPropagation();
    }
    return;
  }

  /**
   * Allows expansion and closing of the internal table.
   * @param event
   * @param row
   * @param expanded
   */
  public changeExpand(event: any, row: any, expanded: boolean): void {
    this.fireEvent(event);
    this.genericTable.rowDetail.collapseAllRows();
    if (!expanded) {
      this.genericTable.rowDetail.toggleExpandRow(row);
    }
  }

  /**
   * Set mouseOut to false
   */
  public mouseOver() {
    this.mouseOut = false;
  }

  /**
   * Set mouseOut to true
   */
  public mouseOutButton() {
    this.mouseOut = true;
  }

  /**
   * Allows you to reload the data.
   */
  public forceReloadData(): void {
    this.reloadEmitter.emit("reload");
  }

  /**
   * If the col column is of type INSTANCEGUID,
   * it returns the name of the corresponding gateway;
   * if it is of type NUMBER it formats the value,
   * otherwise it returns the original value.
   * @param value
   * @param col
   */
  public getValueForColumn(value: any, col: Column): string {
    if (col.Type === ColumnTableType.INSTANCEGUID && value !== "") {
      let gatewayName = this._gatewaysNames.find(
        (g) => g.InstanceGUID === value
      );
      value = gatewayName?.Name;
    } else if (
      col.Type === ColumnTableType.ROUND_NUMBER_2 ||
      col.Type === ColumnTableType.ROUND_NUMBER_5
    ) {
      value = this.roundValue(Number(value), col);
    }
    return value;
  }

  /**
   * When the column address is sorted
   * @param valueA
   * @param valueB
   * @param rowA
   * @param rowB
   * @param sortDirection
   * @returns 0 if valueA==valueB, 1 if valueA<valueB, -1 otherwise
   */
  public onSort(
    valueA: any,
    valueB: any,
    rowA: any,
    rowB: any,
    sortDirection?: any
  ): number {
    let typeCol = this.identifyColumn(valueA, rowA);
    if (typeCol === ColumnTableType.DATE) {
      return StaticClass.SortDateFunction(valueA, valueB);
    }
    return StaticClass.SortAddressFunction(valueA, valueB);
  }

  private roundValue(value: number, col: Column): string {
    let decimal: number = 0;
    switch (col.Type) {
      case ColumnTableType.ROUND_NUMBER_2:
        decimal = 2;
        break;
      case ColumnTableType.ROUND_NUMBER_5:
        decimal = 5;
        break;
    }

    return this._translationService.formatDecimal(value, true, decimal);
  }

  private identifyColumn(value: any, row: any): number {
    for (const [key, val] of Object.entries(row)) {
      if (val === value) {
        return this.tableInfo.Columns.find((c) => c.Prop === key).Type;
      }
    }
    return -1;
  }

  /**
   * Load all gateways present in the data.
   */
  private async loadGateways(): Promise<void> {
    let columnsGateways = this.tableInfo.Columns.filter(
      (c) => c.Type === ColumnTableType.INSTANCEGUID
    );
    if (columnsGateways.length > 0) {
      let timer = setTimeout(async () => {
        for (let i = 0; i < this.tableInfo.List.length; i++) {
          let gateway: GatewayNameForTable = null;
          for (const [key, value] of Object.entries(this.tableInfo.List[i])) {
            if (columnsGateways.find((c) => c.Prop === key)) {
              if (
                value.toString() !== "" &&
                !this._gatewaysNames.find(
                  (g) => g.InstanceGUID === value.toString()
                )
              ) {
                gateway = new GatewayNameForTable();
                gateway.InstanceGUID = value.toString();
                gateway.Name = await this.getGatewayNameFromStore(
                  value.toString()
                );
              }
            }
            if (gateway) {
              this._gatewaysNames.push(gateway);
            }
            gateway = null;
          }
        }
        clearTimeout(timer);
      }, 500);
    }
  }

  /**
   * Returns the gateway that has instanceGUID of GUIDtoSearchFor.
   * @param GUIDtoSearchFor
   */
  private async getGatewayNameFromStore(
    GUIDtoSearchFor: string
  ): Promise<string> {
    const foundGateway = await this._store
      .select(listGateway)
      .pipe(
        filter(
          (listGatewayStore: GenericStoreApiData) =>
            listGatewayStore !== null && listGatewayStore !== undefined
        ),
        filter(
          (listGatewayStore: GenericStoreApiData) =>
            !(
              listGatewayStore.errorGeneric ||
              listGatewayStore.errorNotYetImplemented
            )
        ),
        tap((listGatewayStore: GenericStoreApiData) => {
          if (listGatewayStore.isOldDataInUse) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_store.select(listGateway)",
              "getGatewayNameFromStore()",
              "We are using old data!!"
            );
          }
        }),
        map(
          (listGatewayObject: GenericStoreApiData) =>
            listGatewayObject.data?.data || []
        ),
        take(1),
        map((listGateway: Array<Gateway>) => {
          return listGateway.find((gateway: Gateway) => {
            return gateway.instanceGUID === GUIDtoSearchFor;
          });
        })
      )
      .toPromise();
    return foundGateway.description;
  }

  /**
   * If the starting date is greater than the
   * ending date it causes an error toast to appear.
   * @param startDate starting date
   * @param endDate ending date
   */
  private async checkDates(startDate: Date, endDate: Date): Promise<boolean> {
    if (startDate !== null && endDate !== null) {
      if (startDate > endDate) {
        this._toastService.showToast(ToastType.ERROR, this._lblErrorDate, 5000);
        await this._loaderService.dismissLoaderWaiting();
        return false;
      }
    }
    return true;
  }

  /**
   * Recalculates the size of the table.
   */
  private recalcTable(): void {
    var timer = setTimeout(() => {
      if (this.genericTable) {
        this.genericTable.recalculate();
        if (this.secondTable) {
          this.secondTable.recalculate();
        }
        this.checkTable();
      }
      clearTimeout(timer);
    }, this._intervalResizeCol);
  }

  /**
   * Hide some columns of the table if the screen
   * size is less than a certain threshold.
   */
  private checkTable(): void {
    this.tableInfo.Columns = [];
    for (let i = 0; i < this._columsCopy.length; i++) {
      if (
        (this._columsCopy[i].Visible === HideShowColumn.HIDE_1500 &&
          this.genericTable?._innerWidth() > this._middleWidth) ||
        (this._columsCopy[i].Visible === HideShowColumn.HIDE_1000 &&
          this.genericTable?._innerWidth() > this._smallWidth) ||
        this._columsCopy[i].Visible === HideShowColumn.SHOW ||
        (this._columsCopy[i].Visible === HideShowColumn.SHOW_DETAILS &&
          this.showAllCol)
      ) {
        this.tableInfo.Columns.push(this._columsCopy[i]);
      }
    }
    if (this.tableInfoSecond) {
      this.tableInfoSecond.Columns = [];
      for (let j = 0; j < this._columnsSecondCopy.length; j++) {
        if (
          (this._columnsSecondCopy[j].Visible === HideShowColumn.HIDE_1500 &&
            this.genericTable?._innerWidth() > this._middleWidth) ||
          (this._columnsSecondCopy[j].Visible === HideShowColumn.HIDE_1000 &&
            this.genericTable?._innerWidth() > this._smallWidth) ||
          this._columnsSecondCopy[j].Visible === HideShowColumn.SHOW ||
          (this._columnsSecondCopy[j].Visible === HideShowColumn.SHOW_DETAILS &&
            this.showAllColSecond)
        ) {
          this.tableInfoSecond.Columns.push(this._columnsSecondCopy[j]);
        }
      }
    }
  }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.tableInfo.List = [...this.tableInfo.List];
          this.recalcTable();
        }
      );
  }

  private closeAccordion(): void {
    const nativeEl = this.accordionGroup;
    if (nativeEl.value.includes("first")) {
      nativeEl.value = undefined;
    } else {
      nativeEl.value = ["first"];
    }
  }

  @HostListener("window:resize", ["$event"]) onResize(event) {
    var timeout = setTimeout(() => {
      this.recalcTable();
      clearTimeout(timeout);
    }, this._intervalResizeCol);
  }

  getRowClass = (row) => {
    return this.currentConnectionType != this.connectionType.NONE
      ? "online"
      : "offline";
  };
}
